import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { uploadToCloudinary } from "@/lib/cloudinary";
import useDropImage from "@/hooks/useDropImage";
import { User, KeyRound, Check, X, ShieldCheck, Phone, Send, Loader2, MessageCircle, GraduationCap, Camera } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/api";
import PushNotifSettings from "@/components/PushNotifSettings";
import TelegramCard from "@/components/TelegramCard";
import TelegramGroupsAdmin from "@/components/TelegramGroupsAdmin";
import { useLearnerMode } from "@/components/LearnerHint";

export default function Perfil() {
  const { user, refreshUser } = useAuth();
  const learner = useLearnerMode();
  const isAdmin = (user?.roles || []).some((r) => ["admin", "administracion"].includes(r));
  const [form, setForm] = useState({ current: "", nueva: "", confirma: "" });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  // WhatsApp state
  const [wa, setWa] = useState({ number: "", opt_in: false });
  const [waLoading, setWaLoading] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waStatus, setWaStatus] = useState(null);

  useEffect(() => {
    if (user) setWa({ number: user.whatsapp_number || "", opt_in: !!user.whatsapp_opt_in });
    api.get("/whatsapp/status").then(({ data }) => setWaStatus(data)).catch(() => {});
  }, [user?.id, user?.whatsapp_number, user?.whatsapp_opt_in]);

  const saveWa = async () => {
    setWaLoading(true);
    try {
      await api.patch("/users/me/whatsapp", {
        whatsapp_number: wa.number,
        whatsapp_opt_in: wa.opt_in,
      });
      toast.success("WhatsApp guardado");
      refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando WhatsApp");
    } finally {
      setWaLoading(false);
    }
  };

  const sendTest = async () => {
    setWaSending(true);
    try {
      await api.post("/whatsapp/test", {
        to: wa.number,
        body: "🎉 Mensaje de prueba desde RotulaciónDNF. Tu cuenta WhatsApp está conectada al portal interno.",
      });
      toast.success("Mensaje enviado · revisa tu WhatsApp");
    } catch (err) {
      const detail = err.response?.data?.detail || "Error";
      toast.error(detail);
    } finally {
      setWaSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.nueva !== form.confirma) {
      setMsg({ type: "err", text: "Las contraseñas no coinciden" }); return;
    }
    if (form.nueva.length < 6) {
      setMsg({ type: "err", text: "La nueva contraseña debe tener al menos 6 caracteres" }); return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.nueva,
      });
      setMsg({ type: "ok", text: "Contraseña actualizada correctamente" });
      setForm({ current: "", nueva: "", confirma: "" });
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Error" });
    } finally { setLoading(false); }
  };

  // Subir foto a Cloudinary (usado por el input file y por drag-and-drop)
  const subirFoto = useCallback(async (file) => {
    if (!file) return;
    try {
      toast.info("Subiendo foto...");
      const { secure_url } = await uploadToCloudinary(file, "dnf/users/avatars", "image");
      await api.patch("/users/me", { foto_url: secure_url });
      toast.success("Foto actualizada");
      refreshUser?.();
    } catch (err) {
      toast.error("Error al subir: " + (err?.response?.data?.detail || err.message || err));
    }
  }, [refreshUser]);
  const fotoDrop = useDropImage(subirFoto, { accept: ["image/"] });

  if (!user) return null;

  return (
    <div className="p-8 max-w-3xl">
      <PageHeader title="Mi perfil" subtitle="Datos de tu cuenta y contraseña" />

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 mb-4" data-testid="perfil-info">
        <div className="flex items-center gap-4">
          <label
            className={`relative cursor-pointer group rounded-full transition ${
              fotoDrop.dragging ? "ring-4 ring-amber-400 ring-offset-2 ring-offset-zinc-900" : ""
            }`}
            data-testid="perfil-foto-upload"
            {...fotoDrop.bind}
          >
            {user.foto_url ? (
              <img src={user.foto_url} alt={user.nombre || user.email}
                className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/40" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-amber-500/20 border-2 border-amber-500/40 flex items-center justify-center text-amber-300 text-xl font-bold">
                {(user.nombre || user.email || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/50 flex items-center justify-center transition">
              <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
            </div>
            <input type="file" accept="image/*" className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                await subirFoto(file);
                e.target.value = "";
              }} />
          </label>
          <div className="flex-1">
            <div className="text-lg font-semibold">{user.nombre || user.email}</div>
            <div className="text-sm text-zinc-400">{user.email}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(user.roles || []).map((r) => (
                <span key={r} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                  {ROLE_LABELS?.[r] || r}
                </span>
              ))}
            </div>
          </div>
        </div>
        <a
          href="/mis-acreditaciones"
          data-testid="link-mis-acreditaciones"
          className="mt-4 flex items-center justify-between bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg px-4 py-3 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🪪</span>
            <div>
              <div className="text-sm font-semibold text-amber-200">Mis acreditaciones</div>
              <div className="text-[11px] text-zinc-400">Carrefour, UCAGECI, E-Coordina… tarjetas siempre a mano</div>
            </div>
          </div>
          <span className="text-amber-300">→</span>
        </a>
      </div>

      {/* Modo Aprendiz */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 mb-4" data-testid="learner-mode-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-amber-300 font-semibold">
              <GraduationCap className="w-4 h-4" /> Modo aprendiz
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              Destaca con un punto amber pulsante los botones más importantes de cada módulo durante los próximos 7 días. Ideal para los primeros días usando la app.
            </p>
            {learner.active && (
              <div className="mt-2 text-xs text-amber-400">
                ✨ Activo · quedan <b>{learner.daysLeft} día{learner.daysLeft !== 1 ? "s" : ""}</b>
              </div>
            )}
          </div>
          <div className="shrink-0">
            {learner.active ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await learner.deactivate();
                    toast.success("Modo aprendiz desactivado");
                  } catch {
                    toast.error("No se pudo desactivar");
                  }
                }}
                data-testid="learner-mode-deactivate-btn"
                className="border-zinc-700"
              >
                Desactivar
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={async () => {
                  try {
                    await learner.activate();
                    toast.success("Modo aprendiz activado por 7 días 🎓");
                  } catch {
                    toast.error("No se pudo activar");
                  }
                }}
                data-testid="learner-mode-activate-btn"
                className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold"
              >
                Activar 7 días
              </Button>
            )}
          </div>
        </div>
      </div>


      <form onSubmit={submit} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 space-y-4" data-testid="perfil-form">
        <div className="flex items-center gap-2 text-amber-300 font-semibold">
          <KeyRound className="w-4 h-4" /> Cambiar contraseña
        </div>

        <div>
          <Label className="text-xs">Contraseña actual</Label>
          <Input type="password" required value={form.current}
            data-testid="perfil-current-pwd"
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            className="bg-zinc-950 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs">Nueva contraseña <span className="text-zinc-500">(mín. 6 caracteres)</span></Label>
          <Input type="password" required value={form.nueva}
            data-testid="perfil-new-pwd"
            onChange={(e) => setForm({ ...form, nueva: e.target.value })}
            className="bg-zinc-950 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs">Confirmar nueva contraseña</Label>
          <Input type="password" required value={form.confirma}
            data-testid="perfil-confirm-pwd"
            onChange={(e) => setForm({ ...form, confirma: e.target.value })}
            className="bg-zinc-950 border-zinc-800" />
        </div>

        {msg && (
          <div data-testid="perfil-msg"
            className={`flex items-center gap-2 text-sm rounded p-2 ${
              msg.type === "ok" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40" : "bg-red-500/10 text-red-300 border border-red-500/40"
            }`}>
            {msg.type === "ok" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} {msg.text}
          </div>
        )}

        <Button type="submit" disabled={loading}
          data-testid="perfil-submit"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          <ShieldCheck className="w-4 h-4 mr-2" />
          {loading ? "Guardando…" : "Actualizar contraseña"}
        </Button>
      </form>

      {/* ============ NOTIFICACIONES PUSH PWA ============ */}
      <div className="mt-4" data-testid="perfil-push">
        <PushNotifSettings />
      </div>

      {/* ============ WHATSAPP ============ */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 mt-4" data-testid="perfil-whatsapp">
        <div className="flex items-center gap-2 text-emerald-300 font-semibold mb-3">
          <MessageCircle className="w-4 h-4" /> Notificaciones por WhatsApp
        </div>
        <p className="text-xs text-zinc-400 mb-4">
          Recibirás avisos de leads vencidos, rutas asignadas y aprobaciones pendientes.
          Tu número se guarda en formato internacional (E.164).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs">Número WhatsApp</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l bg-zinc-800 border border-zinc-700 border-r-0 text-zinc-400 text-sm">
                <Phone className="w-3.5 h-3.5" />
              </span>
              <Input
                type="tel"
                value={wa.number}
                onChange={(e) => setWa({ ...wa, number: e.target.value })}
                placeholder="+34 666 12 34 56"
                className="bg-zinc-950 border-zinc-800 rounded-l-none"
                data-testid="wa-number"
              />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Acepta formato +34… o internacional.</div>
          </div>
          <div className="flex items-center gap-3 md:justify-end md:pt-5">
            <Label htmlFor="wa-opt" className="text-sm cursor-pointer">Recibir notificaciones</Label>
            <Switch
              id="wa-opt"
              checked={wa.opt_in}
              onCheckedChange={(v) => setWa({ ...wa, opt_in: v })}
              data-testid="wa-opt"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={saveWa} disabled={waLoading}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-semibold"
            data-testid="wa-save">
            {waLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : "Guardar"}
          </Button>
          <Button onClick={sendTest} disabled={waSending || !wa.number || !user?.whatsapp_number}
            variant="outline"
            className="bg-transparent border-emerald-700 text-emerald-300"
            data-testid="wa-test">
            {waSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : <><Send className="w-4 h-4 mr-2" /> Enviar mensaje de prueba</>}
          </Button>
          {waStatus && (
            <span className={`text-[10px] px-2 py-0.5 rounded ml-auto ${waStatus.configured ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
              {waStatus.configured ? (waStatus.test_mode ? "Sandbox conectado" : "Producción") : "No configurado"}
            </span>
          )}
        </div>

        {waStatus?.test_mode && (
          <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
            <b>⚠️ Modo sandbox de Meta:</b> sólo recibirás mensajes si tu número está añadido como <b>destinatario de prueba</b> en Meta Business Suite → WhatsApp → API Setup. Si recibes el error "Recipient phone number not in allowed list", añade tu número allí primero.
          </div>
        )}
      </div>

      <TelegramCard />

      {isAdmin && <TelegramGroupsAdmin />}

      <MisNotificacionesCard />
    </div>
  );
}function MisNotificacionesCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [categorias, setCategorias] = useState({});
  const [silencios, setSilencios] = useState({});

  useEffect(() => {
    let cancel = false;
    api.get("/me/notif-prefs").then(({ data }) => {
      if (cancel) return;
      setEventos(data.eventos || []);
      setCategorias(data.categorias || {});
      setSilencios(data.prefs?.silencios || {});
    }).catch(() => {}).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const isSilenciado = (key, canal) => (silencios[key] || []).includes(canal);

  const toggle = (key, canal) => {
    setSilencios((prev) => {
      const arr = new Set(prev[key] || []);
      if (arr.has(canal)) arr.delete(canal); else arr.add(canal);
      return { ...prev, [key]: Array.from(arr) };
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put("/me/notif-prefs", { silencios });
      toast.success("Preferencias guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const porCat = {};
  for (const e of eventos) {
    const c = e.categoria || "otros";
    if (!porCat[c]) porCat[c] = [];
    porCat[c].push(e);
  }

  return (
    <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800" data-testid="mis-notif-card">
      <div className="flex items-center gap-2 mb-2 text-zinc-100">
        <span className="text-xl">🔔</span>
        <h2 className="text-lg font-semibold">Mis notificaciones</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        Silencia eventos que no quieras recibir. Solo afecta a tu cuenta — el admin decide qué eventos están activos globalmente.
      </p>

      {loading ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {Object.entries(porCat).map(([cat, evs]) => (
              <div key={cat}>
                <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                  {categorias[cat]?.icon} {categorias[cat]?.label || cat}
                </div>
                <div className="space-y-1.5">
                  {evs.map((ev) => (
                    <div key={ev.key} className="flex items-center justify-between text-sm bg-zinc-950/40 px-3 py-2 rounded border border-zinc-800/60">
                      <div>
                        <div className="text-zinc-100">{ev.label}</div>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <label className="flex items-center gap-1.5 cursor-pointer text-zinc-400 hover:text-zinc-200">
                          <input
                            type="checkbox"
                            checked={!isSilenciado(ev.key, "inapp")}
                            onChange={() => toggle(ev.key, "inapp")}
                            data-testid={`prefs-${ev.key}-inapp`}
                          />
                          In-app
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-zinc-400 hover:text-zinc-200">
                          <input
                            type="checkbox"
                            checked={!isSilenciado(ev.key, "email")}
                            onChange={() => toggle(ev.key, "email")}
                            data-testid={`prefs-${ev.key}-email`}
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-zinc-400 hover:text-zinc-200">
                          <input
                            type="checkbox"
                            checked={!isSilenciado(ev.key, "telegram")}
                            onChange={() => toggle(ev.key, "telegram")}
                            data-testid={`prefs-${ev.key}-telegram`}
                          />
                          Telegram
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer text-zinc-400 hover:text-zinc-200">
                          <input
                            type="checkbox"
                            checked={!isSilenciado(ev.key, "whatsapp")}
                            onChange={() => toggle(ev.key, "whatsapp")}
                            data-testid={`prefs-${ev.key}-whatsapp`}
                          />
                          WhatsApp
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={guardar} disabled={saving} data-testid="btn-save-prefs">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar preferencias
            </Button>
          </div>
        </>
      )}
    </div>
  );

}
